/* mighty-protocol.js — build 2026-04-30 (native-permalink rewrite) */
/* ==========================================================================
   mighty-protocol.js
   Asset for /pages/mighty-protocol — visual interactivity + Meta pixel.

   The CTA is a native <a href="…permalink…">. The browser owns the redirect;
   this script never preventDefaults the click. Per-plan permalinks live in
   each plan block's `permalink_url` schema field; on radio change we just
   re-point the anchor's href.

   Meta events fired here (page-scoped — see isMightyProtocolPage):
     • ViewContent — once when #mp-buybox enters viewport at >=50%
     • AddToCart   — passively on each .mp-buybox__cta click

   AddToCart is missing on permalink redirects (Shopify's Meta sales-channel
   pixel only auto-fires on /cart/add.js POSTs), so we fill it manually here.
   ========================================================================== */

(function () {
  'use strict';

  /* ---------------------------------------------------------------------- *
   * Meta pixel helpers — modeled on /pages/mighty-lp5a-v3 tracking block.
   * Failure-safe: missing fbq → no-op; thrown errors → swallowed; never
   * blocks navigation.
   * ---------------------------------------------------------------------- */
  function isMightyProtocolPage() {
    return /^\/pages\/mighty-protocol\/?$/.test(window.location.pathname);
  }

  function mkEid(label) {
    return 'mp-' + label + '-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 8);
  }

  function fb(track, event, params, opts) {
    if (!window.fbq) return;
    try { window.fbq(track, event, params || {}, opts || {}); } catch (e) {}
  }

  function activeRadio() {
    return document.querySelector('.mp-buybox input[type="radio"][name="mp-plan"]:checked');
  }

  function pixelPayload(radio) {
    var value = radio ? parseFloat(radio.dataset.totalPrice) : 0;
    var variantId = radio && radio.dataset.variantId ? Number(radio.dataset.variantId) : null;
    return {
      content_ids: variantId ? [variantId] : [],
      content_type: 'product',
      value: isFinite(value) ? value : 0,
      currency: 'USD',
    };
  }

  /* AddToCart — passive click listener on the CTA anchor.
     No preventDefault, no setTimeout — browser navigates natively. */
  function hookBuyButton() {
    if (!isMightyProtocolPage()) return;
    var a = document.querySelector('.mp-buybox__cta');
    if (!a || a.dataset.mpHooked) return;
    a.dataset.mpHooked = '1';
    a.addEventListener('click', function () {
      var payload = pixelPayload(activeRadio());
      fb('track', 'AddToCart', payload, { eventID: mkEid('atc') });
    });
  }

  /* ViewContent — fires once when the buy box enters the viewport at >=50%.
     Uses the currently-checked radio (default plan at page-load). */
  function hookViewContent() {
    if (!isMightyProtocolPage()) return;
    if (!('IntersectionObserver' in window)) return;
    var target = document.getElementById('mp-buybox');
    if (!target) return;
    var fired = false;
    var io = new IntersectionObserver(function (entries, obs) {
      entries.forEach(function (e) {
        if (!e.isIntersecting || fired) return;
        fired = true;
        obs.disconnect();
        if (!window.fbq) {
          // fbq may not have bootstrapped yet — retry once after a short delay
          setTimeout(function () { fired = false; io.observe(target); }, 200);
          return;
        }
        var payload = pixelPayload(activeRadio());
        payload.content_name = 'Mighty Protocol';
        fb('track', 'ViewContent', payload, { eventID: mkEid('vc') });
      });
    }, { threshold: 0.5 });
    io.observe(target);
  }

  /* ---------------------------------------------------------------------- *
   * Buybox plan-radio selector + dynamic CTA total/compare/href
   * ---------------------------------------------------------------------- */
  function updateCtaTotals(radio) {
    var totalEl = document.querySelector('.mp-buybox [data-buybox-total]');
    var compareEl = document.querySelector('.mp-buybox [data-buybox-total-compare]');
    var dividerEl = document.querySelector('.mp-buybox .mp-buybox__cta-divider');
    var savingsEl = document.querySelector('.mp-buybox [data-buybox-savings]');
    var savingsAmountEl = document.querySelector('.mp-buybox [data-buybox-savings-amount]');
    var savingsPctEl = document.querySelector('.mp-buybox [data-buybox-savings-pct]');
    if (!radio) return;
    var total = (radio.dataset.totalPrice || '').trim();
    var compare = (radio.dataset.totalCompare || '').trim();
    if (totalEl) totalEl.textContent = total ? '$' + total : '';
    if (compareEl) compareEl.textContent = compare ? '$' + compare : '';
    if (dividerEl) dividerEl.style.display = total ? '' : 'none';

    var totalNum = parseFloat(total);
    var compareNum = parseFloat(compare);
    var savings = (isFinite(totalNum) && isFinite(compareNum) && compareNum > totalNum)
      ? compareNum - totalNum
      : 0;
    if (savingsEl) {
      if (savings > 0) {
        var savingsStr = savings >= 10 ? Math.round(savings).toString() : savings.toFixed(2);
        var pct = Math.round((savings / compareNum) * 100);
        if (savingsAmountEl) savingsAmountEl.textContent = 'YOU SAVE $' + savingsStr;
        if (savingsPctEl) savingsPctEl.textContent = pct + '% OFF';
        savingsEl.hidden = false;
      } else {
        savingsEl.hidden = true;
      }
    }
  }

  function updateCtaHref(radio) {
    if (!radio) return;
    var cta = document.querySelector('.mp-buybox__cta');
    var permalink = (radio.dataset.permalink || '').trim();
    if (cta && permalink) cta.href = permalink;
  }

  /* ---------------------------------------------------------------------- *
   * Modal — generic open/close for [data-mp-modal-open] / [data-mp-modal-close]
   * ---------------------------------------------------------------------- */
  function initModals() {
    var openers = document.querySelectorAll('[data-mp-modal-open]');
    if (!openers.length) return;
    var lastFocus = null;

    function openModal(id) {
      var modal = document.getElementById(id);
      if (!modal) return;
      lastFocus = document.activeElement;
      modal.hidden = false;
      document.documentElement.classList.add('mp-modal-open');
      var closeBtn = modal.querySelector('.mp-modal__close');
      if (closeBtn) closeBtn.focus();
    }
    function closeModal(modal) {
      if (!modal) return;
      modal.hidden = true;
      document.documentElement.classList.remove('mp-modal-open');
      if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus();
    }

    openers.forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        openModal(btn.getAttribute('data-mp-modal-open'));
      });
    });
    document.querySelectorAll('[data-mp-modal-close]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        closeModal(btn.closest('.mp-modal'));
      });
    });
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      var open = document.querySelector('.mp-modal:not([hidden])');
      if (open) closeModal(open);
    });
  }

  function initBuyboxPlanRadios() {
    var radios = document.querySelectorAll('.mp-buybox input[type="radio"][name="mp-plan"]');
    if (!radios.length) return;

    radios.forEach(function (radio) {
      radio.addEventListener('change', function () {
        var box = radio.closest('.mp-buybox');
        if (box) {
          box.querySelectorAll('.mp-buybox__plan').forEach(function (plan) {
            plan.classList.toggle('is-selected', plan.contains(radio) && radio.checked);
          });
        }
        updateCtaTotals(radio);
        updateCtaHref(radio);
      });
    });

    // Initial render: pick up the default-checked radio
    var checked = document.querySelector('.mp-buybox input[type="radio"][name="mp-plan"]:checked');
    if (checked) {
      updateCtaTotals(checked);
      updateCtaHref(checked);
    }
  }

  /* ---------------------------------------------------------------------- *
   * Desktop hero region — wrap hero + title + buybox into a 2-col grid
   * with a sticky right column. CSS handles layout once the wrapper exists.
   * Mobile-first: only runs at ≥1024px viewport.
   * ---------------------------------------------------------------------- */
  function initDesktopHeroLayout() {
    if (window.matchMedia('(max-width: 1023px)').matches) return;
    var main = document.getElementById('MainContent');
    if (!main) return;
    var hero   = main.querySelector('.shopify-section.mp-section-hero-gallery');
    var title  = main.querySelector('.shopify-section.mp-section-title');
    var buybox = main.querySelector('.shopify-section.mp-section-buybox');
    if (!hero || !title || !buybox) return;
    if (main.querySelector('.mp-hero-region')) return; // already wrapped

    var wrap = document.createElement('div');
    wrap.className = 'mp-hero-region';
    var rightCol = document.createElement('div');
    rightCol.className = 'mp-hero-region__right';

    hero.parentNode.insertBefore(wrap, hero);
    wrap.appendChild(hero);
    wrap.appendChild(rightCol);
    rightCol.appendChild(title);
    rightCol.appendChild(buybox);
  }

  /* ---------------------------------------------------------------------- *
   * Hero gallery — dot navigation (mobile swipe is native scroll-snap)
   * ---------------------------------------------------------------------- */
  function initHeroGallery() {
    var galleries = document.querySelectorAll('.mp-hero-gallery');
    galleries.forEach(function (gallery) {
      var track = gallery.querySelector('.mp-hero-gallery__track');
      var dots  = gallery.querySelectorAll('.mp-hero-gallery__dot');
      if (!track || !dots.length) return;

      dots.forEach(function (dot, i) {
        dot.addEventListener('click', function () {
          var slides = track.querySelectorAll('.mp-hero-gallery__slide');
          if (slides[i]) slides[i].scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
        });
      });

      // Update active dot on scroll
      var debounce = null;
      track.addEventListener('scroll', function () {
        if (debounce) cancelAnimationFrame(debounce);
        debounce = requestAnimationFrame(function () {
          var slides = track.querySelectorAll('.mp-hero-gallery__slide');
          var trackCenter = track.scrollLeft + track.clientWidth / 2;
          var activeIdx = 0;
          var minDist = Infinity;
          slides.forEach(function (slide, i) {
            var slideCenter = slide.offsetLeft + slide.offsetWidth / 2;
            var dist = Math.abs(slideCenter - trackCenter);
            if (dist < minDist) { minDist = dist; activeIdx = i; }
          });
          dots.forEach(function (d, i) {
            d.classList.toggle('is-active', i === activeIdx);
            d.setAttribute('aria-current', i === activeIdx ? 'true' : 'false');
          });
        });
      }, { passive: true });
    });
  }

  /* ---------------------------------------------------------------------- *
   * Is-this-for-you checklist — counter + result-text swap
   * ---------------------------------------------------------------------- */
  function initIsThisForYou() {
    var section = document.querySelector('.mp-is-this-for-you');
    if (!section) return;
    var checkboxes = section.querySelectorAll('input[type="checkbox"]');
    var countEl = section.querySelector('.mp-is-this-for-you__count');
    var resultEl = section.querySelector('.mp-is-this-for-you__result');
    if (!checkboxes.length || !resultEl) return;

    var resultZero      = resultEl.dataset.resultZero      || '';
    var resultOne       = resultEl.dataset.resultOne       || '';
    var resultTwo       = resultEl.dataset.resultTwo       || '';
    var resultThreePlus = resultEl.dataset.resultThreePlus || '';

    function copyFor(n) {
      if (n <= 0) return resultZero;
      if (n === 1) return resultOne;
      if (n === 2) return resultTwo;
      return resultThreePlus;
    }

    function update() {
      var n = 0;
      checkboxes.forEach(function (c) { if (c.checked) n++; });
      if (countEl) countEl.textContent = n;
      resultEl.textContent = copyFor(n);
    }

    checkboxes.forEach(function (c) { c.addEventListener('change', update); });
    update();
  }

  /* ---------------------------------------------------------------------- *
   * Scroll-to triggers (data-mp-scroll-to="<name>")
   * Smooth-scrolls to .mp-<name>. Used by buybox CTAs and the title-bar
   * star rating that anchors to the reviews section.
   * ---------------------------------------------------------------------- */
  function initScrollToBuy() {
    var triggers = document.querySelectorAll('[data-mp-scroll-to]');
    if (!triggers.length) return;
    triggers.forEach(function (t) {
      t.addEventListener('click', function (e) {
        var name = t.getAttribute('data-mp-scroll-to');
        if (!name) return;
        var target = document.querySelector('.mp-' + name) || document.getElementById(name);
        if (!target) return;
        if (t.tagName === 'A') e.preventDefault();
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });
  }

  /* ---------------------------------------------------------------------- *
   * Boot
   * ---------------------------------------------------------------------- */
  function boot() {
    initDesktopHeroLayout();
    initBuyboxPlanRadios();
    initHeroGallery();
    initIsThisForYou();
    initScrollToBuy();
    initModals();
    hookBuyButton();
    hookViewContent();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
